import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@repo/ui/components/ui/pagination';

type OpenCallsPaginationProps = {
  page: number;
  limit: number;
  totalCount: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
};

export const OpenCallsPagination = ({
  page,
  limit,
  totalCount,
  loading = false,
  onPageChange,
}: OpenCallsPaginationProps) => {
  const pageCount = Math.ceil(totalCount / limit);
  if (pageCount <= 1) return null;

  const visiblePages = Array.from(
    { length: pageCount },
    (_, index) => index + 1,
  )
    .filter(
      (pageNumber) =>
        pageNumber === 1 ||
        pageNumber === pageCount ||
        Math.abs(pageNumber - page) <= 1,
    )
    .reduce<Array<number | 'ellipsis'>>((pages, pageNumber) => {
      const previousPage = pages.at(-1);
      if (typeof previousPage === 'number' && pageNumber - previousPage > 1) {
        pages.push('ellipsis');
      }
      pages.push(pageNumber);
      return pages;
    }, []);

  const updatePage = (nextPage: number) => {
    if (!loading && nextPage !== page) onPageChange(nextPage);
  };
  const pageHref = (pageNumber: number) =>
    pageNumber === 1 ? '/' : `/?page=${pageNumber}`;

  return (
    <Pagination className="mt-6" aria-label="Open calls pagination">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            aria-disabled={page === 1 || loading}
            href={page === 1 || loading ? undefined : pageHref(page - 1)}
            className={
              page === 1 || loading
                ? 'pointer-events-none opacity-50'
                : 'cursor-pointer'
            }
            onClick={(event) => {
              event.preventDefault();
              updatePage(page - 1);
            }}
          />
        </PaginationItem>
        {visiblePages.map((pageNumber, index) => (
          <PaginationItem key={`${pageNumber}-${index}`}>
            {pageNumber === 'ellipsis' ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                aria-label={`Go to page ${pageNumber}`}
                href={loading ? undefined : pageHref(pageNumber)}
                className={
                  pageNumber === page || loading
                    ? 'cursor-default'
                    : 'cursor-pointer'
                }
                isActive={pageNumber === page}
                onClick={(event) => {
                  event.preventDefault();
                  updatePage(pageNumber);
                }}
              >
                {pageNumber}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}
        <PaginationItem>
          <PaginationNext
            aria-disabled={page === pageCount || loading}
            href={
              page === pageCount || loading ? undefined : pageHref(page + 1)
            }
            className={
              page === pageCount || loading
                ? 'pointer-events-none opacity-50'
                : 'cursor-pointer'
            }
            onClick={(event) => {
              event.preventDefault();
              updatePage(page + 1);
            }}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};
